import React, { useState, useRef } from 'react';
import { Upload, X, CheckCircle } from 'lucide-react';

const Step5 = ({ formData, updateFormData, onSubmit, isSubmitting }) => {
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFiles = (files) => {
    const newFiles = Array.from(files).filter(file => {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      const maxSize = 10 * 1024 * 1024; // 10MB
      
      if (!allowedTypes.includes(file.type)) {
        alert(`Το αρχείο ${file.name} δεν είναι υποστηριζόμενου τύπου. Επιτρέπονται μόνο JPG, PNG και PDF.`);
        return false;
      }
      
      if (file.size > maxSize) {
        alert(`Το αρχείο ${file.name} είναι πολύ μεγάλο. Μέγιστο μέγεθος: 10MB.`);
        return false;
      }
      
      return true;
    });

    updateFormData({
      attachments: [...(formData.attachments || []), ...newFiles]
    });
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const removeFile = (index) => {
    const newFiles = (formData.attachments || []).filter((_, i) => i !== index);
    updateFormData({ attachments: newFiles });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (file) => {
    if (file.type.startsWith('image/')) {
      return '🖼️';
    } else if (file.type === 'application/pdf') {
      return '📄';
    }
    return '📎';
  };

  const requestTypes = {
    'DL1': 'Αντίγραφο ιατρικού φακέλου νοσηλείας',
    'DL2': 'Αντίγραφο ιατρικού φακέλου νοσηλείας θανόντος',
    'DL3': 'Αντίγραφο ιατρικού φακέλου επίσκεψης στα ΤΕΠ',
    'DL4': 'Απεικονιστική εξέταση',
    'DL5': 'Πόρισμα εξέτασης',
    'DL6': 'Μικροβιολογικές εξετάσεις',
    'DL7': 'Πρακτικό χειρουργείου',
    'DL8': 'Τοξικολογικές',
    'DL9': 'Εξιτήριο',
    'DL10': 'Βεβαίωση νοσηλείας',
    'DL11': 'Ιατρική γνωμάτευση νοσηλείας',
    'DL12': 'Συγκεντρωτική κατάσταση νοσηλειών',
    'DL13': 'Υποβολή παραπόνων/θετικών σχολίων',
    'DL14': 'Άλλο'
  };

  return (
    <div>
      <h3>Βήμα 5: Επιβεβαίωση & Υποβολή</h3>
      <p className="mb-20">Ελέγξτε τα στοιχεία σας, προσθέστε τυχόν επιπλέον δικαιολογητικά και υποβάλετε το αίτημά σας.</p>

      {/* Summary */}
      <div className="form-group">
        <h4>Σύνοψη Αιτήματος</h4>
        <div style={{ 
          background: '#f8f9fa', 
          padding: '20px', 
          borderRadius: '8px',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{ marginBottom: '10px' }}>
            <strong>Τύπος Αιτούντος:</strong> {formData.requesterType === 'patient' ? 'Ασθενής' : 'Εκπρόσωπος'}
          </div>
          <div style={{ marginBottom: '10px' }}>
            <strong>Μέθοδος Επιβεβαίωσης:</strong> {formData.identityMethod === 'govgr' ? 'Gov.gr' : 'ΚΕΠ'} - {formData.requesterType === 'patient' ? 'ΥΔ' : 'Εξουσιοδότηση'}
          </div>
          <div style={{ marginBottom: '10px' }}>
            <strong>Είδος Αιτήματος:</strong> {requestTypes[formData.requestType] || formData.requestType}
          </div>
          {formData.patientAmka && (
            <div style={{ marginBottom: '10px' }}>
              <strong>ΑΜΚΑ:</strong> {formData.patientAmka}
            </div>
          )}
          {formData.email && (
            <div style={{ marginBottom: '10px' }}>
              <strong>Email:</strong> {formData.email}
            </div>
          )}
          {formData.phone && (
            <div style={{ marginBottom: '10px' }}>
              <strong>Τηλέφωνο:</strong> {formData.phone}
            </div>
          )}
        </div>
      </div>

      {/* File Upload Area */}
      <div className="form-group">
        <label>Επιπλέον Δικαιολογητικά Αρχεία (προαιρετικό)</label>
        <div
          className={`file-upload-area ${dragActive ? 'dragover' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="file-upload-icon" size={48} />
          <div className="file-upload-text">
            Κάντε κλικ ή σύρετε αρχεία εδώ
          </div>
          <div className="file-upload-hint">
            Επιτρέπονται: JPG, PNG, PDF (μέγιστο 10MB το καθένα)
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".jpg,.jpeg,.png,.pdf"
            onChange={handleFileInput}
            style={{ display: 'none' }}
          />
        </div>

        {/* File List */}
        {(formData.attachments || []).length > 0 && (
          <div className="file-list">
            {(formData.attachments || []).map((file, index) => (
              <div key={index} className="file-item">
                <div className="file-info">
                  <span className="file-icon">{getFileIcon(file)}</span>
                  <span className="file-name">{file.name}</span>
                  <span className="file-size">({formatFileSize(file.size)})</span>
                </div>
                <button
                  type="button"
                  className="remove-file"
                  onClick={() => removeFile(index)}
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirmation */}
      <div className="form-group">
        <div style={{ 
          background: '#e3f2fd', 
          padding: '15px', 
          borderRadius: '8px',
          border: '2px solid #3498db'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <CheckCircle size={20} color="#3498db" style={{ marginRight: '8px' }} />
            <strong>Επιβεβαίωση</strong>
          </div>
          <p style={{ margin: 0, fontSize: '14px' }}>
            Επιβεβαιώνω ότι όλες οι πληροφορίες που παρέχω είναι ακριβείς και πλήρεις. 
            Κατανοώ ότι η παροχή ψευδών πληροφοριών μπορεί να έχει νομικές συνέπειες 
            σύμφωνα με την ισχύουσα νομοθεσία.
          </p>
        </div>
      </div>

      {/* Protocol Number Info */}
      <div className="form-group">
        <div style={{ 
          background: '#fff3cd', 
          padding: '15px', 
          borderRadius: '8px',
          border: '2px solid #ffc107',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            <strong style={{ color: '#856404' }}>📋 Αριθμός Πρωτοκόλλου</strong>
          </div>
          <p style={{ margin: 0, fontSize: '14px', color: '#856404' }}>
            Μετά την υποβολή του αιτήματός σας, θα σας αποδοθεί αυτόματα ένας μοναδικός 
            <strong> αριθμός πρωτοκόλλου</strong> που θα εμφανιστεί στην οθόνη επιβεβαίωσης. 
            Κρατήστε αυτόν τον αριθμό για μελλοντική αναφορά και επικοινωνία με τη Γραμματεία.
          </p>
        </div>
      </div>

      <div className="alert alert-info">
        <strong>📧 Μετά την υποβολή:</strong> Θα λάβετε email επιβεβαίωσης στη διεύθυνση που δηλώσατε. 
        Το αίτημά σας θα επεξεργαστεί εντός 5-7 εργάσιμων ημερών και θα λάβετε τα αιτούμενα 
        έγγραφα ηλεκτρονικά.
      </div>
    </div>
  );
};

export default Step5;








